import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Client, WorkSite } from '@/types';
import { ClientService } from '@/services/clientService';
import { generateClientId, generateWorkSiteId } from '@/utils/idGenerator';
import { validateClient, formatValidationErrors } from '@/utils/validation';
import { useToast } from '@/contexts/ToastContext';
import { queryKeys } from '@/hooks/queries/queryKeys';

/**
 * Hooks con mutaciones de clientes (P0.2).
 * Usado por DataProvider para reducir God Object.
 */
export function useClientMutations(companyId: string) {
    const queryClient = useQueryClient();
    const { showToast } = useToast();

    const invalidateClients = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: queryKeys.clients() });
        queryClient.invalidateQueries({ queryKey: queryKeys.orders() });
    }, [queryClient]);

    const saveClient = useCallback(
        async (clientData: Partial<Client>, tempWorkSites: WorkSite[], editingClient: Client | null) => {
            const validationErrors = validateClient(clientData);
            if (validationErrors.length > 0) {
                showToast(formatValidationErrors(validationErrors), 'error');
                throw new Error('Validation failed');
            }
            try {
                if (editingClient) {
                    const updatedClient = { ...editingClient, ...clientData, workSites: tempWorkSites } as Client;
                    await ClientService.updateClient(updatedClient);
                    invalidateClients();
                } else {
                    const newClient: Client = {
                        id: generateClientId(),
                        companyId,
                        name: clientData.name || 'Nuevo Cliente',
                        phone: clientData.phone || '',
                        email: clientData.email || '',
                        createdAt: new Date().toISOString(),
                        workSites: tempWorkSites,
                    };
                    await ClientService.createClient(newClient);
                    invalidateClients();
                }
            } catch (error: any) {
                console.error('Failed to save client', error);
                showToast(error.message || 'Error al guardar el cliente', 'error');
                throw error;
            }
        },
        [companyId, invalidateClients, showToast]
    );

    const createClientFromOrder = useCallback(
        async (client: Client): Promise<Client | undefined> => {
            try {
                const clientWithCompany = { ...client, companyId };
                const savedClient = await ClientService.createClient(clientWithCompany);
                invalidateClients();
                return savedClient;
            } catch (error: any) {
                console.error('Failed to create client from order', error);
                showToast(error.message || 'Error al guardar el cliente desde orden', 'error');
                throw error;
            }
        },
        [companyId, invalidateClients]
    );

    const addWorkSiteToClient = useCallback(
        async (clientId: string, siteName: string, siteAddress?: string, locationDetails?: NonNullable<WorkSite['locationDetails']>): Promise<WorkSite> => {
            const newSite: WorkSite = {
                id: generateWorkSiteId(),
                companyId,
                name: siteName,
                address: siteAddress,
                locationDetails,
            };
            try {
                const savedSite = await ClientService.addWorkSite(clientId, newSite);
                invalidateClients();
                return savedSite || newSite;
            } catch (error: any) {
                console.error('Failed to add work site', error);
                showToast(error?.message || 'Error al añadir la obra', 'error');
                throw error;
            }
        },
        [companyId, invalidateClients, showToast]
    );

    const deleteClient = useCallback(
        async (clientId: string) => {
            try {
                await ClientService.deleteClient(clientId);
                invalidateClients();
            } catch (error) {
                console.error('Failed to delete client', error);
            }
        },
        [invalidateClients]
    );

    return {
        saveClient,
        createClientFromOrder,
        addWorkSiteToClient,
        deleteClient,
    };
}
